import { useEffect, useRef, useState } from 'react';

/**
 * useState whose value is mirrored to localStorage under `key`. Lazy-reads the
 * stored value on mount (falling back to `initial` when missing or unparseable)
 * and writes back on every change. All localStorage access is guarded so a
 * private-mode / quota / garbage-value failure degrades to in-memory state
 * rather than crashing the app.
 */
export function usePersistentState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw) as T;
    } catch {
      /* ignore — fall back to initial */
    }
    return initial;
  });

  // Avoid re-writing on the very first render with the freshly-read value.
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* ignore — non-persistent this session */
    }
  }, [key, value]);

  return [value, setValue] as const;
}
